<script setup>

    import { useRouter } from 'vue-router'

    import { ref, computed, watch, onMounted, onUnmounted } from 'vue'

    import { storeToRefs } from 'pinia'
    import { useAuthStore, useConfigStore, useEngagementStore, useClientStore, useStaffStore } from '@/Stores'

    import { getModifiedProperties, parseAddressComponents } from '@/Helpers'

    import { IconGift24Filled } from '@iconify-prerendered/vue-fluent'

    import InputLabel from '@/Components/Forms/InputLabel.vue'
    import AddressAutocomplete from '@/Components/Forms/AddressAutocomplete.vue'
    import LwxSelect from '@/Components/Forms/LwxSelect.vue'
    import LatteStaffLabel from '@/Components/LatteStaffLabel.vue'
    import StaffMultiSelect from '@/Components/Forms/StaffMultiSelect.vue'
    import Modal from '@/Components/App/Modal.vue'
    import SecondaryButton from '@/Components/Forms/SecondaryButton.vue'
    import PrimaryButton from '@/Components/Forms/PrimaryButton.vue'
    import DangerButton from '@/Components/Forms/DangerButton.vue'
    import EngagementGiftPicker from '@/Components/Forms/EngagementGiftPicker.vue'
    import { ExclamationTriangleIcon } from '@heroicons/vue/24/outline'
    import Tag from '@/Components/Tag.vue'
    import LatteRatingBar from '@/Components/Feedback/LatteRatingBar.vue'
    import LwxSwitch from '@/Components/Forms/LwxSwitch.vue'

    import { fullName, util } from '@/Helpers/util'
    import TextInputV0 from '@/Components/Forms/TextInputV0.vue'

    const authStore = useAuthStore()
    const { user } = storeToRefs(authStore)
    const configStore = useConfigStore()
    const { config } = storeToRefs(configStore)
    const engagementStore = useEngagementStore()
    const { selectedGiftRequest, selectedGiftRequestLoading } = storeToRefs(engagementStore)
    const staffStore = useStaffStore()
    const { activeStaffs } = storeToRefs(staffStore)

    const emit = defineEmits(['engagementGiftRequest:create', 'engagementGiftRequest:update', 'engagementGiftRequest:delete'])

    const props = defineProps({
        scope: {
            type: Array,
            default: () => [ 'all' ] // Possible values: 'leads', 'prospects', 'clients', 'all'
        },
        columns: {
            type: Array,
            default: () => [ 'latte-id', 'status', 'name', 'location', 'contact-actions', 'created_at' ] // Possible values: 'latte-id', 'status', 'type', 'name', 'location', 'contact-actions', 'birthday', 'created_at'
        }
    })

    const router = useRouter()

    const loading = ref(true)

    const confirmationModalVisible = ref(false)
    const confirmationModalText = ref('')
    const confirmationModalAction = ref('Delete')
    const confirmationModalCancelLabel = ref('Cancel')
    let confirmationModalFunction = null

    const minMessageLength = 50
    const maxMessageLength = 250

    const states = ref([
        {
            id: null,
            name: ''
        }
    ])

    function defaultFromStaff() {
        const staff = activeStaffs.value.find(staff => staff.id === user.value.user_id);
        return staff ? [{ ...staff, name: fullName(staff.first_name, staff.last_name) }] : [];
    }

    const engagementGiftRequestData = ref({
        id: null,
        gift_id: 1,
        from: defaultFromStaff(),
        message: '',
        address: '',
        address_2: '',
        city: '',
        state: states.value.find(s => s.id === null) ? { ...states.value.find(s => s.id === null) } : null,  // Ensure state is a plain object
        zip: '',
        address_validity: null,
        occasion_id: 1,
        occasion: "Thanksgiving Holiday 2025",
        propagate_address: true,
        default: true
    })

    const localEngagementGiftRequestData = ref({ ...engagementGiftRequestData.value })

    const hasValidatedGiftRequestData = computed(() => {
        return localEngagementGiftRequestData.value.gift_id !== null &&
               localEngagementGiftRequestData.value.from.length > 0 &&
               localEngagementGiftRequestData.value.address !== '' &&
               localEngagementGiftRequestData.value.city !== '' &&
               localEngagementGiftRequestData.value.state !== null &&
               localEngagementGiftRequestData.value.zip?.length === 5 &&
               !isNaN(localEngagementGiftRequestData.value.zip) &&
               trimmedMessageLength.value >= minMessageLength &&
               trimmedMessageLength.value <= maxMessageLength
    })

    const trimmedMessageLength = computed(() => {
        return localEngagementGiftRequestData.value.message.trim().length
    })

    watch(config, (value) => {
        states.value = value.states;
    }, { deep: true, immediate : true })

    watch(selectedGiftRequest, (newGiftRequest) => {
        if (newGiftRequest) {
            const fromStaff = newGiftRequest.id ? newGiftRequest.from ? newGiftRequest.from.map(fromStaff => {
                const staff = activeStaffs.value.find(s => s.id === fromStaff.id);
                return staff ? { ...staff, name: `${staff.first_name} ${staff.last_name}`.trim() } : null;
            }).filter(Boolean) : (() => {
                const staff = activeStaffs.value.find(s => s.id === user.value.user_id);
                return staff ? [{ ...staff, name: `${staff.first_name} ${staff.last_name}`.trim() }] : [];
            })() : defaultFromStaff()

            engagementGiftRequestData.value = {
                id: newGiftRequest.id,
                gift_id: newGiftRequest.gift ? newGiftRequest.gift.id : engagementGiftRequestData.value.gift_id,
                from: fromStaff,
                message: newGiftRequest.message,
                address: newGiftRequest.address.address,
                address_2: newGiftRequest.address.address_2,
                city: newGiftRequest.address.city,
                state: newGiftRequest.address.state ? { ...states.value.find(s => s.id === newGiftRequest.address.state) } : null,
                zip: newGiftRequest.address.zip,
                address_validity: null,
                occasion_id: newGiftRequest.occasion ? newGiftRequest.occasion.id : engagementGiftRequestData.value.occasion_id,
                occasion: newGiftRequest.occasion ? newGiftRequest.occasion.name : engagementGiftRequestData.value.occasion,
                propagate_address: newGiftRequest.propagate_address,
                recipient_id: newGiftRequest.recipient.id,
                recipient_type: newGiftRequest.recipient.type,
            }

            if (newGiftRequest.recipient.type === 'contact') {
                engagementGiftRequestData.value.recipient_parent_id = newGiftRequest.recipient.parent.id
                engagementGiftRequestData.value.recipient_parent_type = newGiftRequest.recipient.parent.type
            }

            console.log('Loaded Engagement Gift Request Data:', engagementGiftRequestData.value)
            localEngagementGiftRequestData.value = { ...engagementGiftRequestData.value }
        }
    }, { immediate: true })

    function addressFill(place) {
        const addressData = parseAddressComponents(place)
        Object.assign(localEngagementGiftRequestData.value, addressData)
    }

    function handleAddressUpdate(value) {
        if (typeof value === 'object' && value.place) {
            addressFill(value.place)
        } else {
            localEngagementGiftRequestData.value.address = value
        }
    }

    const isEdit = computed(() => {
        return selectedGiftRequest.value?.id !== null
    })

    const closeOnEscape = (e) => {
        if (e.key === 'Escape') {
            confirmRequestFormCancel()
        }
    }

    onMounted(() => {
        document.addEventListener('keydown', closeOnEscape)
    })

    onUnmounted(() => {
        document.removeEventListener('keydown', closeOnEscape)
    })


    const confirmRequestDeletion = () => {
        confirmationModalVisible.value = true
        confirmationModalText.value = 'Are you sure you want to delete this engagement request?'
        confirmationModalAction.value = 'Delete Request'
        confirmationModalFunction = handleDelete
    }

    const confirmRequestFormCancel = () => {

        console.log('Confirming Request Form Cancel')
        console.log(engagementGiftRequestData.value)
        console.log(selectedGiftRequest.value)

        let modifiedData = getModifiedProperties(
            JSON.stringify(localEngagementGiftRequestData.value),
            JSON.stringify(engagementGiftRequestData.value),
            [
                'address', 'address_2', 'city',
                {
                    key: 'state',
                    diff: (newObj, oldObj) => {
                        return JSON.stringify(newObj) !== JSON.stringify(oldObj) ? newObj : null
                    }
                },
                'zip', 'message', 'gift_id', 'from'
            ]
        )

        console.log('Modified Data on Cancel:', modifiedData)
        if (modifiedData && Object.keys(modifiedData).length  > 0) {
            confirmationModalVisible.value = true
            confirmationModalText.value = 'Are you sure you want to cancel? Unsaved changes will be lost.'
            confirmationModalAction.value = 'Discard Changes'
            confirmationModalCancelLabel.value = 'Continue Editing'
            confirmationModalFunction = handleCancel
        } else {
            handleCancel()
        }

    }

    const closeModal = () => {
        confirmationModalVisible.value = false
    }

    function handleDelete() {
        // Logic to delete the gift goes here
        confirmationModalVisible.value = false
        selectedGiftRequest.value = null
        emit('engagementGiftRequest:delete', engagementGiftRequestData.value.id)
    }

    function handleCancel() {
        selectedGiftRequest.value = null
        console.log('Handling Form Cancel Request')
    }

    function handleSave() {
        localEngagementGiftRequestData.value.message = localEngagementGiftRequestData.value.message.trim()
        if (localEngagementGiftRequestData.value.message.length < minMessageLength ||
            localEngagementGiftRequestData.value.message.length > maxMessageLength) {
            return
        }
        console.log('Handling Form Save Request', localEngagementGiftRequestData.value, engagementGiftRequestData.value, isEdit.value)
        if (!isEdit.value) {
            emit('engagementGiftRequest:create', localEngagementGiftRequestData.value)
        } else {
            let modifiedData = getModifiedProperties(
                JSON.stringify(localEngagementGiftRequestData.value),
                JSON.stringify(engagementGiftRequestData.value),
                [
                    'address', 'address_2', 'city',
                    {
                        key: 'state',
                        diff: (newObj, oldObj) => {
                            return JSON.stringify(newObj) !== JSON.stringify(oldObj) ? newObj : null
                        }
                    },
                    'zip', 'message', 'gift_id', 'from', 'propagate_address'
                ]
            )
            emit('engagementGiftRequest:update', engagementGiftRequestData.value.id, modifiedData)
        }
        selectedGiftRequest.value = null
    }

    const typeToColor = {
        client: 'green',
        caregiver: 'blue',
        contact: 'yellow',
    }

    let currentAddressDiffStr = JSON.stringify({})
    watch(localEngagementGiftRequestData, (newData) => {
        let modifiedData = getModifiedProperties(
            JSON.stringify(newData),
            JSON.stringify(engagementGiftRequestData.value),
            [
                'address', 'address_2', 'city', 'zip', {
                    key: 'state',
                    diff: (newObj, oldObj) => {
                        return JSON.stringify(newObj) !== JSON.stringify(oldObj) ? newObj : null
                    }
                },
            ]
        )

        if (currentAddressDiffStr !== JSON.stringify(modifiedData)) {
            if (Object.keys(modifiedData).length != 0) {
                localEngagementGiftRequestData.value.propagate_address = true
            }
            currentAddressDiffStr = JSON.stringify(modifiedData)
        }
    }, { deep: true })
</script>

<template>
    <div v-if="selectedGiftRequestLoading" class="min-h-64 flex justify-center items-center">
        Loading...
    </div>
    <div class="divide-y divide-gray-400/30" v-if="!selectedGiftRequestLoading">

        <div class="flex gap-4 p-5 bg-lifeworx-blue-600/5">

            <div class="bg-white size-14 rounded-lg border border-gray-400/30 flex justify-center items-center">
                <IconGift24Filled class="size-6 text-lifeworx-blue-600" />
            </div>

            <div class="flex items-center">
                <div class="flex flex-col -mt-0.5">
                    <h2 class="text-sm font-bold text-lifeworx-blue-600 mb-1">
                        {{ isEdit ? 'Edit Scheduled Engagement' : 'Schedule an Engagement' }}
                    </h2>
                    <p class="text-xs text-lifeworx-blue-900">
                        Show appreciation and build relationships with timely gifts.
                    </p>
                </div>
            </div>

        </div>

        <div class="px-6 py-4 grid grid-cols-1 md:grid-cols-2 gap-7">

            <div class="flex flex-col gap-y-2">
                <h3 class="text-xs font-bold text-lifeworx-blue-800">
                    Recipient
                </h3>
                <div class="h-96 p-5 rounded-md border border-dashed border-gray-400/30">

                    <div class="flex gap-2">

                        <span class="text-base font-semibold text-lifeworx-blue-900">
                            {{ selectedGiftRequest?.recipient?.first_name + ' ' + selectedGiftRequest?.recipient?.last_name }}
                        </span>

                        <Tag
                            v-if="selectedGiftRequest?.recipient?.type != 'coi'"
                            class="text-xs"
                            :color="typeToColor[selectedGiftRequest?.recipient?.type]"
                        >
                            {{ selectedGiftRequest?.recipient?.type }}
                        </Tag>
                        <Tag
                            v-if="selectedGiftRequest?.is_coi === true"
                            class="text-xs"
                            color="purple"
                        >
                            COI
                        </Tag>

                    </div>

                    <template v-if="selectedGiftRequest?.recipient?.type === 'client' || selectedGiftRequest?.recipient?.type === 'caregiver'">
                        <div class="w-60 -ml-0.5 mt-1">
                            <LatteRatingBar
                                :subject="selectedGiftRequest?.recipient"
                                :subjectType="selectedGiftRequest?.recipient?.type"
                            />
                        </div>
                    </template>

                    <template v-if="selectedGiftRequest?.recipient?.type === 'contact'">
                        <div class="flex mt-1 gap-x-1 text-sm font-semibold text-lifeworx-blue-900 items-center">
                            <span class="text-stone-400">for</span>
                            {{ selectedGiftRequest?.recipient?.parent?.first_name + ' ' + selectedGiftRequest?.recipient?.parent?.last_name }}
                            <Tag
                                class="inline-block text-xs mx-1"
                                color="green"
                            >Client</Tag>
                        </div>
                        <div class="w-60 -ml-0.5 mt-1">
                            <LatteRatingBar
                                :subject="selectedGiftRequest?.recipient?.parent"
                                subjectType="client"
                            />
                        </div>
                    </template>

                    <div class="mt-1 mb-6">
                        <LatteStaffLabel
                            :actionable="false"
                            :staff="selectedGiftRequest?.recipient?.staff || selectedGiftRequest?.recipient?.parent?.staff "
                            size="sm"
                        />
                    </div>

                    <div class="w-full grid grid-cols-4 gap-2">
                        <div class="col-span-4">
                            <InputLabel
                                for="recipient-address"
                                value="Address"
                                class="text-xs text-stone-400"
                            >
                                <AddressAutocomplete
                                    v-model="localEngagementGiftRequestData.address"
                                    @update:modelValue="handleAddressUpdate"
                                    :validity="localEngagementGiftRequestData.address_validity"
                                    placeholder=""
                                    input_class="text-gray-900 placeholder:text-stone-400 focus:ring-lifeworx-blue-600 focus:border-lifeworx-blue-600 text-xs placeholder:text-xs"
                                    :validate="['required']"
                                    :invalid="!localEngagementGiftRequestData.address"
                                />
                            </InputLabel>
                        </div>
                        <div class="col-span-4">
                            <InputLabel
                                for="recipient-address2"
                                value="Address 2"
                                class="text-xs text-stone-400"
                            >
                                <TextInputV0
                                    class="w-full px-2.5 py-1.5 text-sm text-stone-700"
                                    id="recipient-address2"
                                    type="text"
                                    v-model="localEngagementGiftRequestData.address_2"
                                />
                            </InputLabel>
                        </div>
                        <div class="col-span-2">
                            <InputLabel
                                for="recipient-city"
                                value="Town/City"
                                class="text-xs text-stone-400"
                            >
                                <TextInputV0
                                    class="w-full px-2.5 py-1.5 text-sm text-stone-700"
                                    id="recipient-city"
                                    type="text"
                                    v-model="localEngagementGiftRequestData.city"
                                    :validate="['required']"
                                    :invalid="!localEngagementGiftRequestData.city"
                                />
                            </InputLabel>
                        </div>
                        <div class="">
                            <InputLabel
                                for="recipient-state"
                                value="State"
                                class="text-xs text-stone-400"
                            >
                                <LwxSelect
                                    id="recipient-state"
                                    v-model="localEngagementGiftRequestData.state"
                                    :options="states"
                                    :useId="true"
                                    :validate="['required']"
                                    :invalid="!localEngagementGiftRequestData.state"
                                    input_class="text-stone-700"
                                />
                            </InputLabel>
                        </div>
                        <div class="">
                            <InputLabel
                                for="recipient-zip"
                                value="ZIP"
                                class="text-xs text-stone-400"
                            >
                                <TextInputV0
                                    class="w-full px-2.5 py-1.5 text-sm text-stone-700"
                                    id="recipient-zip"
                                    type="text"
                                    :validate="['required', 'len:5', 'number']"
                                    v-model="localEngagementGiftRequestData.zip"
                                    :invalid="!localEngagementGiftRequestData.zip || localEngagementGiftRequestData.zip?.length !== 5 || isNaN(localEngagementGiftRequestData.zip)"
                                />
                            </InputLabel>
                        </div>
                        <div class="col-span-4 mt-4">
                            <InputLabel
                                for="propagate-address"
                                :value="`Save Updated Address to ${ selectedGiftRequest?.recipient?.type === 'contact' ? 'Client Contact' : util.titleCase(selectedGiftRequest?.recipient?.type) }`"
                                class="text-sm text-stone-400 flex gap-2 font-semibold"
                                position="right"
                            >
                                <LwxSwitch
                                    v-model="localEngagementGiftRequestData.propagate_address"
                                    id="propagate-address"
                                    palette="blue"
                                />
                            </InputLabel>
                        </div>
                    </div>

                </div>
            </div>

            <div class="flex flex-col gap-y-2">
                <h3 class="text-xs font-bold text-lifeworx-blue-800">
                    Details
                </h3>
                <div class="h-96 p-5 rounded-md border border-dashed border-gray-400/30 flex flex-col gap-5">

                    <TextInputV0
                        class="w-full px-2.5 py-1.5 text-sm text-stone-700 font-semibold"
                        id="occasion"
                        type="text"
                        v-model="localEngagementGiftRequestData.occasion"
                        disabled
                    />

                    <EngagementGiftPicker
                        v-model="localEngagementGiftRequestData.gift_id"
                    />

                    <div class="relative flex-auto">
                        <Textarea
                            class="w-full text-sm text-stone-700 resize-none h-full"
                            id="message"
                            type="text"
                            v-model="localEngagementGiftRequestData.message"
                            placeholder="Add a personal message to include with the gift..."
                            :pt="{
                                root: '!max-h-auto',
                            }"
                            :invalid="trimmedMessageLength > 0 && (trimmedMessageLength < minMessageLength || trimmedMessageLength > maxMessageLength)"
                        />
                        <div
                            :class="[
                                'text-xs text-right absolute bottom-2 right-1 bg-white/90 p-1.5 rounded-md font-bold',
                                trimmedMessageLength < minMessageLength || trimmedMessageLength > maxMessageLength ? 'text-lifeworx-red-500' : 'text-lifeworx-green-500',

                            ]">
                                {{ trimmedMessageLength < minMessageLength ? `${trimmedMessageLength}/${minMessageLength}` : '' }}
                                {{ trimmedMessageLength >= minMessageLength && trimmedMessageLength <= maxMessageLength ? `${trimmedMessageLength}/${maxMessageLength}` : '' }}
                                {{ trimmedMessageLength > maxMessageLength ? `${trimmedMessageLength}/${maxMessageLength}` : '' }}
                        </div>
                    </div>
                    <StaffMultiSelect
                        v-model="localEngagementGiftRequestData.from"
                        :validate="['required']"
                    />
                </div>
            </div>


        </div>

        <div class="h-20 p-5 flex items-center justify-between">
            <div>
                <span class="text-sm text-stone-600">
                    <!-- Footer text goes here. -->
                </span>
            </div>
            <div class="flex gap-4">
                <PrimaryButton
                    v-if="isEdit"
                    class="w-32"
                    @click="confirmRequestDeletion"
                    button_style="bg-lifeworx-red-500 hover:bg-lifeworx-red-800 focus:bg-lifeworx-red-500 active:bg-lifeworx-red-800 focus:ring-lifeworx-red-500 text-white"
                >
                    Delete
                </PrimaryButton>

                <SecondaryButton
                    class="w-32"
                    @click="confirmRequestFormCancel"
                    color="red"
                >
                    Cancel
                </SecondaryButton>

                <PrimaryButton
                    class="w-32"
                    @click="handleSave"
                    :disabled="!hasValidatedGiftRequestData"
                >
                    Save
                </PrimaryButton>
            </div>
        </div>
    </div>

    <Modal
        :show="confirmationModalVisible"
        maxWidth="md"
    >
        <div class="w-full p-6 grid gap-8">

            <div class="size-24 mx-auto bg-lifeworx-red-500/10 rounded-full flex justify-center items-center">
                <ExclamationTriangleIcon class="size-12 text-lifeworx-red-500 mx-auto" />
            </div>

            <h2 class="text-lg text-center font-semibold text-lifeworx-blue-800 dark:text-gray-100">
                {{ confirmationModalText }}
            </h2>

            <div class="flex flex-col gap-5 justify-center">

                <DangerButton
                    class="h-14"
                    @click="confirmationModalFunction"
                >
                    {{ confirmationModalAction }}
                </DangerButton>

                <SecondaryButton
                    class="h-14"
                    @click="closeModal"
                >
                    {{ confirmationModalCancelLabel || 'Cancel' }}
                </SecondaryButton>

            </div>
        </div>
    </Modal>

</template>
